// src/content/insights/authentic-assessment-ai-era.jsx

import Image from "../../../components/insights/Image";
import Quote from "../../../components/insights/Quote";
import Citation from "../../../components/insights/Citation";
import References from "../../../components/insights/References";

export const article = {
  slug: "authentic-assessment-ai-era",

  meta: {
    title: "Why Auditory-Perceptual Training May Become One of the Most Valuable Forms of Authentic Assessment in the AI Era",
    publishedAt: "2026-06-06",
    category: "Education",
    
    author: [
      {
        name: "Neus Calaf",
        role: "Founder, All-Voiced",
        // avatar: "/images/authors/neus.jpg"
      }
    ],

    excerpt:
      "As generative AI transforms higher education, universities are increasingly searching for forms of assessment that reflect genuine human performance. Auditory-perceptual evaluation offers a surprisingly powerful example.",

    coverImage: "/images/insights/2026/authentic-assessment-ai-era/student-listening.png",

    references: {
      ref1:
        "Pommée, T., Younes, S., Gauthier, L., & Verduyckt, I. (2025). Improving auditory-perceptual voice assessment: A hybrid in-class and online CAPE-V training study. Journal of Voice. https://doi.org/10.1016/j.jvoice.2025.11.029",
      },
  },

  tags: ["education", "artificial-intelligence", "assessment", "voice-science", "perception"],

  related: [],

  wordCount: 1103,

  Component: function Article({ references }) {
    return (
      <>

        <p>
          Higher education is entering a period of profound uncertainty.
        </p>

        <p>
          For decades, universities have relied heavily on written assignments,
          reports, essays, reflections, and case analyses as evidence of student
          learning. Today, many of these activities can be partially automated
          through generative AI systems.
        </p>

        <p>
          This does not necessarily mean students are learning less. But it does
          force institutions to reconsider a fundamental question:
        </p>

        <Quote>
          What kinds of activities provide credible evidence that learning has
          actually taken place?
        </Quote>

        <p>
          The search for authentic assessment has become one of the defining
          educational challenges of the AI era.
        </p>

        <h2>
          Not all expertise can be outsourced
        </h2>

        <p>
          Many professional competencies ultimately depend on human judgment.
        </p>

        <p>
          Consider, for example:
        </p>

       <ul>
        <li>a conservator identifying subtle signs of deterioration in a painting,</li>
        <li>a sommelier distinguishing between similar wines,</li>
        <li>a conductor detecting balance problems within an orchestra,</li>
        <li>or a clinician distinguishing between subtle variations in vocal quality.</li>
      </ul>

        <p>
          These activities share an important characteristic.
        </p>

        <Quote>
          Before a judgment can be made, perception must occur.
        </Quote>

        <p>
          The activity cannot be completed without engaging the relevant
          perceptual skills.
        </p>

        <h2>
          Listening as evidence
        </h2>

        <p>
          Auditory-perceptual voice evaluation offers a particularly revealing example.
        </p>

        <p>
          Students are asked to listen to voice samples and make judgments about
          qualities such as roughness, breathiness, or strain.
        </p>

        <p>
          To perform these tasks successfully, they must develop internal
          perceptual references through repeated exposure and practice.
        </p>

        <p>
          Unlike many written activities, the learning process cannot be reduced
          to producing text. The learner must actually listen.
        </p>

        <p>
          Every response reflects a moment of human perception.
        </p>

        <h2>
          Beyond grading
        </h2>

        <p>
          This observation becomes even more interesting when digital
          infrastructures are introduced.
        </p>

        <p>
          Modern platforms can record:
        </p>

        <ul>
          <li>listening decisions,</li>
          <li>response consistency,</li>
          <li>agreement with reference evaluators,</li>
          <li>patterns of improvement over time,</li>
          <li>and interaction behaviours during training.</li>
        </ul>

        <p>
          Assessment is no longer limited to a final score.
        </p>

        <p>
          The learning process itself becomes observable.
        </p>

        <p>
          When listening activities are digitally structured, they do more
          than support practice. They can generate learning analytics:
          evidence of participation, consistency, calibration, progress, and
          developing perceptual expertise.
        </p>

        <Image
          src="/images/insights/2026/authentic-assessment-ai-era/students-analytics-demo.png"
          caption="Illustrative demo showing how digitally structured listening activities can generate learning analytics related to participation, consistency, calibration, and perceptual development."
        />

        <Quote>
          The most valuable educational data may not be what students write, but
          what they repeatedly perceive and how those perceptions evolve.
        </Quote>

        <p>
          If this is true, then perceptual training environments are not merely
          teaching tools. They become environments where perceptual expertise can 
          be developed, monitored, and documented over time.
        </p>

        <h2>
          From concept to practice
        </h2>

        <p>
          This idea is no longer purely theoretical.
        </p>

        <p>
          A recent study conducted within a graduate-level Voice Disorders course at
          the Université de Montréal explored a hybrid training model combining
          classroom instruction, structured perceptual calibration, and online
          auditory-perceptual training.
        </p>

        <p>
          The online component was implemented through a dedicated auditory-perceptual
          training platform, allowing students to continue practicing beyond the
          classroom while generating structured data about their listening performance
          and training activity.
        </p>

        <p>
          Following training, students demonstrated significant improvements in
          auditory-perceptual accuracy, with gains maintained over time.
        </p>

        <p>
          The study also suggested that online practice could provide additional
          benefits for some perceptually challenging voice qualities while supporting
          continued calibration toward expert reference ratings.
        </p>

        <p>
          The project ultimately resulted in a peer-reviewed publication in the
          <em>Journal of Voice <Citation id="ref1" /></em>.
        </p>

        <Image
        src="/images/insights/2026/authentic-assessment-ai-era/Montreal.png"
        caption="Educational implementation of All-Voiced within a graduate-level Voice Disorders course at the Université de Montréal. Photo: Ingrid Verduyckt."
        />

        <p>
          While this represents only a single educational implementation, it
          illustrates how perceptual training environments can support learning,
          assessment, and educational research within the same educational setting.
        </p>

        <p>
          More importantly, every listening decision generated during training can
          contribute to a broader understanding of human perception, learning, and
          professional judgment.
        </p>

        <h2>
          From training to understanding expertise development
        </h2>

        <p>
          Historically, perceptual training has been difficult to scale.
        </p>

        <p>
          It often depends on expert instructors, limited teaching materials,
          and highly localized educational experiences.
        </p>

        <p>
          Digital infrastructures create a different possibility. When listening
          activities are structured, learning trajectories can be documented and
          perceptual judgments can generate reusable educational and research data.
        </p>

        <p>
          When these activities are systematically collected, documented, and
          connected across learners, courses, institutions, programs, and time, 
          they do more than support individual training.
        </p>

        <p>
          They can contribute to a deeper understanding of how perceptual
          expertise develops, how evaluators calibrate, and how professional
          judgment evolves.
        </p>

        <Quote>
          Education, assessment, and knowledge generation no longer need to
          exist separately.
        </Quote>

        <h2>
          A broader shift in professional education
        </h2>

        <p>
          Voice evaluation is only one example.
        </p>

        <p>
          Similar principles may apply across many professional domains where
          expertise depends on perception and judgment:
        </p>

        <ul>
          <li>interpreting medical images,</li>
          <li>recognizing clinically relevant patterns,</li>
          <li>assessing quality and performance,</li>
          <li>evaluating risk and uncertainty,</li>
          <li>making complex diagnostic decisions,</li>
          <li>identifying subtle perceptual differences that cannot be reduced to simple rules.</li>
        </ul>

        <p>
          In all of these areas, authentic expertise emerges through repeated
          encounters with complex human decisions rather than through content
          production alone.
        </p>

        <p>
          Across these domains, the underlying challenge is remarkably similar. How do
          we create environments where learners can repeatedly exercise judgment? How
          do we document the development of expertise? And how do we generate evidence
          that meaningful learning has occurred?
        </p>

        <p>
          Structured perceptual learning environments offer one possible answer.
        </p>

        <p>
          Importantly, such environments do not need to be tied to a single curriculum 
          or educational model. Shared infrastructures may allow educators, researchers, 
          and institutions to develop their own programs while contributing to a broader 
          understanding of expertise development across contexts.
        </p>

        <h2>
          Conclusion
        </h2>

        <p>
          Universities are currently searching for assessment models that remain
          meaningful in a world increasingly shaped by artificial intelligence.
        </p>

        <p>
          Some of the most promising answers may not come from new technologies,
          but from uniquely human capacities.
        </p>

        <Quote>
          In an era of automated content generation, the ability to perceive,
          interpret, and judge may become one of the most valuable forms of
          evidence that learning has truly occurred.
        </Quote>

        <p>
          Auditory-perceptual training offers a compelling example of what that
          future might look like.
        </p>

        <p>
          If digital infrastructures can capture, document, and connect human
          judgments at scale, authentic assessment, learning analytics, and
          research on expertise development may become part of the same broader
          challenge: understanding how expertise develops.
        </p>

        <Quote>
          The future of authentic assessment may not be built around what learners
          can produce, but around what they can genuinely perceive.
        </Quote>

        {/* REFERENCES */}

        <References items={references} />
      </>
    );
  },
};